import { Link } from "@tanstack/react-router";
import { ArrowRight, LifeBuoy, MessageCircle, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ContactForm } from "@/features/contact/modules/contact-form/contact-form";
import { Section, SectionHead } from "@/features/platform/components/landing-ui";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

/**
 * Reaching the platform, not a trainer on it. A visitor looking for their own coach wants
 * /<slug>/contact; this page is for the people who run the software, so it says so first.
 */
const CHANNELS: {
  id: string;
  icon: LucideIcon;
  title: () => string;
  body: () => string;
  link?: { to: string; label: () => string };
}[] = [
  {
    id: "help",
    icon: LifeBuoy,
    title: () => m.contact_aside_help_title(),
    body: () => m.contact_aside_help_body(),
    link: { to: "/help", label: () => m.contact_aside_help_cta() },
  },
  {
    id: "sales",
    icon: MessageCircle,
    title: () => m.platform_contact_sales_title(),
    body: () => m.platform_contact_sales_body(),
  },
  {
    id: "privacy",
    icon: ShieldCheck,
    title: () => m.platform_contact_privacy_title(),
    body: () => m.platform_contact_privacy_body(),
  },
];

/** `lead` when it opens the page, which makes its title the page's h1. */
export const PlatformContact = ({ lead = false }: { lead?: boolean }) => (
  <Section id="contact" className="pt-12 md:pt-16">
    <SectionHead
      as={lead ? "h1" : "h2"}
      eyebrow={m.landing_nav_contact()}
      title={m.platform_contact_title()}
      aside={m.platform_contact_aside()}
    />

    <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
      <ContactForm />
      <div className="grid content-start gap-3">
        {CHANNELS.map((channel) => {
          const Icon = channel.icon;
          return (
            <div key={channel.id} className="flex gap-4 rounded-[28px] border border-border bg-card p-6">
              <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
                <Icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <h3 className="text-lg font-medium tracking-[-0.02em]">{channel.title()}</h3>
                <p className="mt-1 text-[15px] leading-normal text-muted-foreground">{channel.body()}</p>
                {channel.link ? (
                  <Link
                    to={channel.link.to}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-[var(--site-ink)]"
                  >
                    {channel.link.label()}
                    <ArrowRight aria-hidden className="size-4" />
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="mt-3 flex flex-col justify-between gap-6 rounded-[28px] bg-[var(--site-wash)] p-7 md:flex-row md:items-center md:p-10">
      <div>
        <h3 className="text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.1] font-medium tracking-[-0.03em]">
          {m.platform_contact_client_title()}
        </h3>
        <p className="mt-2 max-w-[52ch] text-[15px] leading-normal text-muted-foreground">
          {m.platform_contact_client_body({ app: appConfig.name })}
        </p>
      </div>
      <Link
        to="/login"
        className="inline-flex h-11 shrink-0 items-center justify-center rounded-full bg-foreground px-6 text-sm font-medium text-background transition-transform active:scale-[0.98]"
      >
        {m.landing_nav_sign_in()}
      </Link>
    </div>
  </Section>
);
